// frontend/context/RestaurantContext.tsx

"use client";

import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";
import { useAuth, SubscriptionInfo, SubscriptionAccess } from "./AuthContext";
import { apiFetch } from "../lib/api";

/* =========================
   Types
========================= */
export type Restaurant = {
    id: number;
    name: string;
    slug: string;
    logo_url?: string | null;
    language: string;
    thank_you_email_enabled?: boolean;
    allow_reservation_language?: boolean;
    has_custom_emails?: boolean;
    subscription?: SubscriptionInfo;
    subscription_access?: SubscriptionAccess;
    onboarding_completed?: boolean;
};

export type ReservationSource = {
    id: number;
    name: string;
    is_active: boolean;
    order: number;
};

/* =========================
   Context type
========================= */
type RestaurantContextType = {
    restaurants: Restaurant[];
    restaurant: Restaurant | null;
    loading: boolean;
    setRestaurant: (restaurant: Restaurant) => void;

    // 🔹 Orígenes de reserva
    reservationSources: ReservationSource[];
    sourcesLoading: boolean;

    // 🔹 Idioma actual
    language: string;

    // 🔹 Estado de suscripción
    subscriptionAccess: SubscriptionAccess | null;
    isSubscriptionBlocked: boolean;
};

/* =========================
   Context
========================= */
const RestaurantContext = createContext<RestaurantContextType | undefined>(
    undefined
);

/* =========================
   Provider
========================= */
export function RestaurantProvider({ children }: { children: ReactNode }) {
    const { user, restaurants: authRestaurants, activeRestaurant } = useAuth();

    const [restaurant, setRestaurantState] = useState<Restaurant | null>(null);

    // 🔹 Orígenes de reserva
    const [reservationSources, setReservationSources] = useState<ReservationSource[]>([]);
    const [sourcesLoading, setSourcesLoading] = useState(false);

    const loading = false; // ⬅️ ya no hay fetch aquí

    // 🔹 Idioma del restaurante (default: español)
    const language = restaurant?.language ?? "es";

    // 🔹 Estado de suscripción
    const subscriptionAccess = restaurant?.subscription_access ?? null;
    const isSubscriptionBlocked = subscriptionAccess ? !subscriptionAccess.allowed : false;

    const setRestaurant = (r: Restaurant | typeof authRestaurants[0]) => {
        // Si viene de authRestaurants, tiene subscription; si viene de Restaurant, no
        const mapped = 'subscription' in r ? mapToRestaurant(r as typeof authRestaurants[0]) : r;
        setRestaurantState(mapped);
        if (mapped) localStorage.setItem("restaurantSlug", mapped.slug);
    };

    // Helper para mapear RestaurantAccess a Restaurant con has_custom_emails
    const mapToRestaurant = (r: typeof authRestaurants[0] | null): Restaurant | null => {
        if (!r) return null;
        return {
            id: r.id,
            name: r.name,
            slug: r.slug,
            logo_url: r.logo_url,
            language: r.language ?? "es",
            thank_you_email_enabled: r.thank_you_email_enabled,
            allow_reservation_language: r.allow_reservation_language ?? false,
            has_custom_emails: r.subscription?.features?.has_custom_emails ?? false,
            subscription: r.subscription,
            subscription_access: r.subscription_access,
            onboarding_completed: (r as any).onboarding_completed, // Temporary cast if AuthContext type isn't updated yet
        };
    };

    useEffect(() => {
        if (!authRestaurants || authRestaurants.length === 0) {
            setRestaurantState(null);
            return;
        }
        const savedSlug = localStorage.getItem("restaurantSlug");
        const found =
            (savedSlug &&
                authRestaurants.find((r) => r.slug === savedSlug)) ||
            null;
        const selected = found ?? activeRestaurant ?? authRestaurants[0];
        setRestaurantState(mapToRestaurant(selected));
    }, [authRestaurants, activeRestaurant]);

    // ======================================================
    // Cargar orígenes de reserva cuando cambia restaurante
    // ======================================================
    useEffect(() => {
        async function loadSources() {
            if (!user || !restaurant) {
                setReservationSources([]);
                return;
            }

            // Si está en onboarding, no cargar orígenes (evita 404/errores innecesarios)
            if (restaurant.onboarding_completed === false) {
                setReservationSources([]);
                return;
            }

            setSourcesLoading(true);

            try {
                const res = await apiFetch("/reservation-sources", {
                    method: "GET",
                    userId: user.id,
                    restaurantSlug: restaurant.slug,
                });

                if (!res.ok) {
                    console.error("Error cargando orígenes de reserva");
                    setReservationSources([]);
                    return;
                }

                const data = await res.json();
                setReservationSources(data);
            } catch (err) {
                console.error("Error de conexión cargando orígenes", err);
                setReservationSources([]);
            } finally {
                setSourcesLoading(false);
            }
        }

        loadSources();
    }, [user, restaurant]);

    return (
        <RestaurantContext.Provider
            value={{
                restaurants: authRestaurants,
                restaurant,
                loading,
                setRestaurant,

                // 🔹 Orígenes de reserva
                reservationSources,
                sourcesLoading,

                // 🔹 Idioma actual
                language,

                // 🔹 Estado de suscripción
                subscriptionAccess,
                isSubscriptionBlocked,
            }}
        >
            {children}
        </RestaurantContext.Provider>
    );
}

/* =========================
   Hook
========================= */
export function useRestaurant() {
    const ctx = useContext(RestaurantContext);
    if (!ctx) {
        throw new Error(
            "useRestaurant debe usarse dentro de RestaurantProvider"
        );
    }
    return ctx;
}